import React, { useState } from 'react';
import SupervisorLayout from '../../components/SupervisorLayout';
import LotteryCodeField from '../../components/LotteryCodeField';
import SupervisorGroupCodeField from '../../components/SupervisorGroupCodeField';
import SupervisorBankCodeField from '../../components/SupervisorBankCodeField';
import DateField from '../../components/DateField';
import { fetchSupervisorSalesList } from '../../api/supervisor';
import { todayISO } from '../../utils/date';
import './SupervisorSalesListPage.css';

const money = (n) => (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const columnize = (arr, cols) => {
  const rows = Math.ceil(arr.length / cols) || 1;
  const out = Array.from({ length: cols }, () => []);
  arr.forEach((item, i) => { out[Math.floor(i / rows)].push(item); });
  return out.filter((c) => c.length);
};

const SupervisorSalesListPage = () => {
  const [date, setDate] = useState(todayISO());
  const [lottery, setLottery] = useState('');
  const [lotteryName, setLotteryName] = useState('');
  // Filtre Groupe / Banque optionnel, mutuellement exclusif (vide = toutes ses banks).
  const [groupId, setGroupId] = useState('');
  const [groupName, setGroupName] = useState('');
  const [bankId, setBankId] = useState('');
  const [bankName, setBankName] = useState('');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const search = async (e) => {
    e.preventDefault();
    if (!lottery) { setError('Choisis une loterie.'); return; }
    setLoading(true);
    setError('');
    try {
      setData(await fetchSupervisorSalesList({ tirage: lottery, date, group: groupId, bank: bankId }));
    } catch (err) {
      setError('Recherche impossible.');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SupervisorLayout>
      <h2 className="svsl-title">Liste des ventes</h2>

      <div className="svsl-card">
        <form onSubmit={search} className="svsl-form">
          <div className="svsl-row">
            <label className="svsl-label">Date</label>
            <DateField className="svsl-input" value={date} onChange={setDate} />
          </div>

          <div className="svsl-row">
            <label className="svsl-label">Loterie</label>
            <LotteryCodeField
              value={lottery}
              name={lotteryName}
              onChange={(code) => { setLottery(code); setLotteryName(''); }}
              onResolve={(code, nm) => { setLottery(code); setLotteryName(nm); }}
              inputClass="svsl-input svsl-input-sm"
              iconClass="svsl-icon-btn"
              nameClass="svsl-scope-name"
            />
          </div>

          <div className="svsl-row">
            <label className="svsl-label">Groupe</label>
            <SupervisorGroupCodeField
              value={groupId}
              name={groupName}
              onChange={(id) => { setGroupId(id); setGroupName(''); }}
              onResolve={(id, nm) => {
                setGroupId(id); setGroupName(nm);
                if (id) { setBankId(''); setBankName(''); }
              }}
              inputClass="svsl-input svsl-input-sm"
              iconClass="svsl-icon-btn"
              nameClass="svsl-scope-name"
            />

            <label className="svsl-label svsl-label-to">Banque</label>
            <SupervisorBankCodeField
              value={bankId}
              name={bankName}
              onChange={(id) => { setBankId(id); setBankName(''); }}
              onResolve={(id, nm) => {
                setBankId(id); setBankName(nm);
                if (id) { setGroupId(''); setGroupName(''); }
              }}
              inputClass="svsl-input svsl-input-sm"
              iconClass="svsl-icon-btn"
              nameClass="svsl-scope-name"
            />
          </div>

          {error && <p className="svsl-error">{error}</p>}
          <button type="submit" className="svsl-search-btn" disabled={loading}>
            {loading ? 'Recherche...' : 'Chercher'}
          </button>
        </form>
      </div>

      {data && (
        <div className="svsl-card svsl-results">
          <p className="svsl-sub"><strong>{data.tirage_name}</strong>{data.tirage_close && <> · fermeture {data.tirage_close}</>}</p>

          {data.products.length === 0 && <p className="svsl-empty">Aucune vente pour ces critères.</p>}

          {data.products.map((p) => (
            <section key={p.code} className="svsl-product">
              <h3 className="svsl-product-name">{p.name}</h3>
              <div className="svsl-cols">
                {columnize(p.numbers, 3).map((col, ci) => (
                  <table key={ci} className="svsl-col-table">
                    <thead><tr><th>N°</th><th>Vendu</th><th>×</th></tr></thead>
                    <tbody>
                      {col.map((n) => (
                        <tr key={n.numero}>
                          <td>{n.numero}</td>
                          <td className="svsl-money">{money(n.vendu)}</td>
                          <td>{n.paris}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ))}
              </div>
              <div className="svsl-product-total">
                <span>Paris : {p.total_paris}</span>
                <span>Total {p.name} : <strong>{money(p.total_vendu)}</strong></span>
              </div>
            </section>
          ))}

          <div className="svsl-grand">
            <div><span>Ventes brutes</span><span>{money(data.brut)}</span></div>
            <div><span>Commission</span><span>{money(data.commission)}</span></div>
            <div className="svsl-grand-net"><span>Ventes nettes</span><span>{money(data.net)}</span></div>
          </div>
        </div>
      )}
    </SupervisorLayout>
  );
};

export default SupervisorSalesListPage;
